'use client'

import { useCallback, useEffect, useState } from 'react'
import { Coach } from '@/payload-types'
import ProfilePicture from '@/blocks/ProfilePicture/Component'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { MoonLoader } from 'react-spinners'
import _ from 'lodash'
import { useInView } from 'react-intersection-observer'
import { cn } from '@/utilities/cn'
import { compareScreen } from '@/lib/common/utils/compareScreen'
import { useTailwindScreen } from '@/lib/client/hooks/useTailwindScreen'

type Response = {
  docs: Coach[]
  totalDocs: number
  totalPages: number
  page: number
  prevPage: number
  nextPage: number
  hasPrevPage: boolean
  hasNextPage: boolean
}

export default function CoachListBlock({ excludeCoachSlug }: { excludeCoachSlug?: string | null }) {
  const [coaches, setCoaches] = useState([] as Coach[])
  const [loading, setLoading] = useState(true)
  const { ref, inView } = useInView({
    threshold: 0,
  })
  // const itemsPerPage = screenFits('lg') ? 5 : screenFits('md') ? 4 : 3
  // const range = screenFits('lg') ? 3 : 2
  const screen = useTailwindScreen()
  const itemsPerPage = compareScreen(screen, '>=', 'lg')
    ? 5
    : compareScreen(screen, '>=', 'md')
      ? 4
      : 3
  const range = compareScreen(screen, '>=', 'lg') ? 3 : 2
  const [startPos, setStartPos] = useState(0)

  // Load all coaches once
  useEffect(() => {
    if (!inView || coaches.length) return
    const where = excludeCoachSlug ? '&where[slug][not_equals]=' + excludeCoachSlug : ''
    fetch('/api/coaches?limit=1000' + where)
      .then((res) => res.json())
      .then((res) => {
        setCoaches(res.docs || [])
        setLoading(false)
      })
  }, [excludeCoachSlug, inView, coaches.length])

  const page = startPos / itemsPerPage + 1
  const totalPages = Math.ceil(coaches.length / itemsPerPage)
  const firstPage = Math.max(page - range, 1)
  const lastPage = Math.min(page + range, totalPages)

  const loadPage = useCallback(
    (page: number) => {
      setStartPos((page - 1) * itemsPerPage)
    },
    [itemsPerPage],
  )

  return (
    <div ref={ref} className={'container mx-auto my-32'}>
      <h2 className={'text-4xl text-center text-primary mb-12'}>Ontmoet de coaches</h2>
      <div className={cn('mt-8 grid gap-4', 'grid-cols-' + itemsPerPage)}>
        {loading && (
          <div className={cn('col-span-' + itemsPerPage, 'flex justify-center')}>
            <MoonLoader color={'#f28dbe'} loading={true} size={60} />
          </div>
        )}

        {!loading &&
          coaches.slice(startPos, startPos + itemsPerPage).map((coach) => (
            <Link key={coach.id} href={'/coach/' + coach.slug}>
              <div className={'flex flex-col gap-4 items-center'}>
                <ProfilePicture media={coach.picture} />
                <div className={'flex flex-col items-center'}>
                  <h3 className={'text-primary text-center'}>{coach.name}</h3>
                  <div className={'min-h-6'}>
                    {coach.city?.length ? (
                      <span className="font-normal text-gray-500 dark:text-gray-400">
                        {coach.city?.join(', ')}
                      </span>
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}

        <div className={cn('col-span-' + itemsPerPage, 'flex gap-2 justify-center mt-4')}>
          {totalPages > 0 &&
            _.range(firstPage, lastPage + 1).map((buttonPage, i) => (
              <Button
                disabled={buttonPage < 1 || buttonPage > totalPages}
                className={cn((buttonPage < 1 || buttonPage > totalPages) && '!opacity-0')}
                key={'page' + i}
                variant={buttonPage === page ? 'default' : 'outline'}
                onClick={() => loadPage(buttonPage)}
              >
                {buttonPage > 0 ? buttonPage : '0'}
              </Button>
            ))}
        </div>
      </div>
    </div>
  )
}
